import React, { useState, useRef, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  Star,
  Plus,
  SpinnerGap,
  Camera,
  Receipt,
  X,
  Check,
  Scan,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format } from 'date-fns';

const AddVisitModal = ({ open, onOpenChange, restaurantId, onSuccess, editVisit = null }) => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const fileInputRef = useRef(null);
  const editVisitRef = useRef(null);

  // Capture editVisit on open so it survives state changes during submit
  useEffect(() => {
    if (open && editVisit) {
      editVisitRef.current = editVisit;
    } else if (!open) {
      editVisitRef.current = null;
    }
  }, [open, editVisit]);

  const isEditing = !!editVisit;

  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [extractedItems, setExtractedItems] = useState([]);
  const [showExtractedItems, setShowExtractedItems] = useState(false);

  // Form state
  const [visitDate, setVisitDate] = useState(new Date());
  const [mealType, setMealType] = useState('dinner');
  const [overallRating, setOverallRating] = useState(0);
  const [foodRating, setFoodRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [ambienceRating, setAmbienceRating] = useState(0);
  const [valueRating, setValueRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [dishes, setDishes] = useState('');
  const [drinks, setDrinks] = useState('');
  const [billTotal, setBillTotal] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [occasionTags, setOccasionTags] = useState([]);
  const [billPhoto, setBillPhoto] = useState(null);
  const [billPreview, setBillPreview] = useState(null);
  const [journalEntry, setJournalEntry] = useState('');
  const [vibe, setVibe] = useState('');
  const [companions, setCompanions] = useState('');
  const [favouriteDishes, setFavouriteDishes] = useState('');

  // Pre-populate form when editing
  useEffect(() => {
    if (editVisit && open) {
      setVisitDate(editVisit.visited_at ? new Date(editVisit.visited_at) : new Date());
      setMealType(editVisit.meal_type || 'dinner');
      setOverallRating(editVisit.overall_rating || 0);
      setFoodRating(editVisit.food_rating || 0);
      setServiceRating(editVisit.service_rating || 0);
      setAmbienceRating(editVisit.ambience_rating || 0);
      setValueRating(editVisit.value_rating || 0);
      setNotes(editVisit.notes || '');
      setDishes((editVisit.dishes_ordered || []).join(', '));
      setDrinks((editVisit.drinks || []).join(', '));
      setBillTotal(editVisit.bill_total ? String(editVisit.bill_total) : '');
      setTipAmount(editVisit.tip_amount ? String(editVisit.tip_amount) : '');
      setPartySize(editVisit.party_size ? String(editVisit.party_size) : '2');
      setOccasionTags(editVisit.occasion_tags || []);
      setJournalEntry(editVisit.journal_entry || '');
      setVibe(editVisit.vibe || '');
      setCompanions((editVisit.companions || []).join(', '));
      setFavouriteDishes((editVisit.favourite_dishes || []).join(', '));
    }
  }, [editVisit, open]);

  const occasionOptions = ['date_night', 'family', 'friends', 'business', 'celebration', 'solo'];
  const vibeOptions = ['romantic', 'celebration', 'casual', 'business', 'family', 'adventure'];

  const resetForm = () => {
    setVisitDate(new Date());
    setMealType('dinner');
    setOverallRating(0);
    setFoodRating(0);
    setServiceRating(0);
    setAmbienceRating(0);
    setValueRating(0);
    setNotes('');
    setDishes('');
    setDrinks('');
    setBillTotal('');
    setTipAmount('');
    setPartySize('2');
    setOccasionTags([]);
    setBillPhoto(null);
    setBillPreview(null);
    setScanning(false);
    setExtractedItems([]);
    setShowExtractedItems(false);
    setJournalEntry('');
    setVibe('');
    setCompanions('');
    setFavouriteDishes('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleBillUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBillPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setBillPreview(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleScanBill = async () => {
    if (!billPhoto) return;
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', billPhoto);
      const response = await api.post('/bills/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const data = response.data;

      // Auto-populate fields from scan
      if (data.total_bill) setBillTotal(data.total_bill.toString());
      if (data.tip) setTipAmount(data.tip.toString());
      if (data.items?.length > 0) {
        setExtractedItems(data.items);
        setDishes(data.items.map(i => i.name).filter(Boolean).join(', '));
      }
      if (data.bill_date) {
        try {
          setVisitDate(new Date(data.bill_date));
        } catch {}
      }

      toast.success(`Bill scanned! ${data.items?.length || 0} items extracted.`);
    } catch (error) {
      console.error('Bill scan error:', error);
      toast.error('Failed to scan bill');
    } finally {
      setScanning(false);
    }
  };

  const toggleOccasion = (tag) => {
    setOccasionTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      toast.error('Please add an overall rating');
      return;
    }

    setLoading(true);
    try {
      const visitData = {
        visited_at: visitDate.toISOString(),
        meal_type: mealType,
        overall_rating: overallRating,
        food_rating: foodRating || null,
        service_rating: serviceRating || null,
        ambience_rating: ambienceRating || null,
        value_rating: valueRating || null,
        notes: notes.trim() || null,
        dishes_ordered: dishes ? dishes.split(',').map(d => d.trim()).filter(Boolean) : [],
        drinks: drinks ? drinks.split(',').map(d => d.trim()).filter(Boolean) : [],
        bill_total: billTotal ? parseFloat(billTotal) : null,
        tip_amount: tipAmount ? parseFloat(tipAmount) : null,
        party_size: partySize ? parseInt(partySize) : null,
        occasion_tags: occasionTags,
        journal_entry: journalEntry.trim() || null,
        vibe: vibe || null,
        companions: companions ? companions.split(',').map(c => c.trim()).filter(Boolean) : [],
        favourite_dishes: favouriteDishes ? favouriteDishes.split(',').map(d => d.trim()).filter(Boolean) : [],
      };

      let visit;
      const editRef = editVisitRef.current;
      if (editRef) {
        const response = await api.put(`/visits/${editRef.id}`, visitData);
        visit = response.data;
        toast.success('Visit updated!');
      } else {
        const response = await api.post(`/restaurants/${restaurantId}/visits`, visitData);
        visit = response.data;

        // Upload bill photo if present (only for new visits)
        if (billPhoto) {
          const formData = new FormData();
          formData.append('file', billPhoto);
          formData.append('attachment_type', 'bill_photo');
          
          await api.post(`/visits/${visit.id}/attachments?attachment_type=bill_photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          try {
            setExtracting(true);
            await api.post(`/visits/${visit.id}/bill-extract`);
          } catch (error) {
            console.log('Bill extraction skipped:', error);
          } finally {
            setExtracting(false);
          }
        }
      }

      onSuccess(visit);
      handleClose();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} visit:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'log'} visit`);
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div>
      <Label className="text-sm text-[#7A7873]">{label}</Label>
      <div className="flex gap-1 mt-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star === value ? 0 : star)}
            className="rating-star"
          >
            <Star
              weight={star <= value ? 'fill' : 'regular'}
              className={`w-6 h-6 ${star <= value ? 'text-[#A94438]' : 'text-[#E8DCD0]'}`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="add-visit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isEditing ? 'Edit Visit' : 'Log Visit'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Date & Meal Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1.5"
                    data-testid="visit-date-btn"
                  >
                    {format(visitDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={visitDate}
                    onSelect={(date) => date && setVisitDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Meal Type</Label>
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger className="mt-1.5" data-testid="meal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="brunch">Brunch</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="drinks">Drinks</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Overall Rating */}
          <div className="p-4 bg-[#F9F8F5] rounded-lg">
            <StarRating
              value={overallRating}
              onChange={setOverallRating}
              label="Overall Rating *"
            />
          </div>

          {/* Sub-ratings */}
          <div className="grid grid-cols-2 gap-4">
            <StarRating value={foodRating} onChange={setFoodRating} label="Food" />
            <StarRating value={serviceRating} onChange={setServiceRating} label="Service" />
            <StarRating value={ambienceRating} onChange={setAmbienceRating} label="Ambience" />
            <StarRating value={valueRating} onChange={setValueRating} label="Value" />
          </div>

          {/* Occasion tags */}
          <div>
            <Label>Occasion</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {occasionOptions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleOccasion(tag)}
                  className={`occasion-tag transition-colors ${
                    occasionTags.includes(tag) 
                      ? 'bg-[#A94438] text-white' 
                      : 'hover:bg-[#E8DCD0]'
                  }`}
                  data-testid={`occasion-${tag}`}
                >
                  {tag.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* What you had */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dishes">Dishes (comma separated)</Label>
              <Input
                id="dishes"
                placeholder="e.g., Tasting menu, Wagyu..."
                value={dishes}
                onChange={(e) => setDishes(e.target.value)}
                className="mt-1.5"
                data-testid="dishes-input"
              />
            </div>
            <div>
              <Label htmlFor="drinks">Drinks (comma separated)</Label>
              <Input
                id="drinks"
                placeholder="e.g., House red, Champagne..."
                value={drinks}
                onChange={(e) => setDrinks(e.target.value)}
                className="mt-1.5"
                data-testid="drinks-input"
              />
            </div>
          </div>

          {/* Spend */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bill">Total Bill (R)</Label>
              <Input
                id="bill"
                type="number"
                placeholder="0"
                value={billTotal}
                onChange={(e) => setBillTotal(e.target.value)}
                className="mt-1.5"
                data-testid="bill-input"
              />
            </div>
            <div>
              <Label htmlFor="tip">Tip (R)</Label>
              <Input
                id="tip"
                type="number"
                placeholder="0"
                value={tipAmount}
                onChange={(e) => setTipAmount(e.target.value)}
                className="mt-1.5"
                data-testid="tip-input"
              />
            </div>
            <div>
              <Label htmlFor="party">Party Size</Label>
              <Input
                id="party"
                type="number"
                placeholder="2"
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                className="mt-1.5"
                data-testid="party-input"
              />
            </div>
          </div>

          {/* Bill photo */}
          <div>
            <Label>Bill Photo</Label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleBillUpload}
              accept="image/*"
              className="hidden"
            />
            {billPreview ? (
              <div className="space-y-2 mt-2">
                <div className="relative">
                  <img
                    src={billPreview}
                    alt="Bill preview"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setBillPhoto(null);
                      setBillPreview(null);
                      setExtractedItems([]);
                    }}
                    className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"
                  >
                    <X weight="bold" className="w-4 h-4" />
                  </button>
                </div>
                <Button
                  type="button"
                  onClick={handleScanBill}
                  disabled={scanning}
                  variant="outline"
                  className="w-full border-[#A94438] text-[#A94438] hover:bg-[#A94438]/5"
                  data-testid="scan-uploaded-bill-btn"
                >
                  {scanning ? (
                    <>
                      <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Receipt weight="bold" className="w-4 h-4 mr-2" />
                      Scan & Auto-fill
                    </>
                  )}
                </Button>

                {/* Extracted items toggle */}
                {extractedItems.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowExtractedItems(!showExtractedItems)}
                      className="flex items-center gap-2 text-sm font-medium text-[#5A5752] hover:text-[#1A1918]"
                      data-testid="toggle-extracted-items"
                    >
                      {showExtractedItems ? <CaretUp weight="bold" className="w-3 h-3" /> : <CaretDown weight="bold" className="w-3 h-3" />}
                      {extractedItems.length} items extracted from bill
                    </button>
                    {showExtractedItems && (
                      <div className="mt-1 space-y-0.5 max-h-[150px] overflow-y-auto" data-testid="extracted-items">
                        {extractedItems.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs py-1 px-2 bg-[#F9F8F5] rounded">
                            <span className="text-[#5A5752]">
                              {item.qty > 1 && <span className="text-[#9A9590] mr-1">{item.qty}x</span>}
                              {item.name}
                            </span>
                            <span className="font-medium text-[#1A1918]">
                              R{typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="dropzone w-full mt-2"
                data-testid="upload-bill-btn"
              >
                <Receipt weight="duotone" className="w-10 h-10 text-[#7A7873] mx-auto mb-2" />
                <p className="text-sm text-[#7A7873]">Click to upload bill photo</p>
                <p className="text-xs text-[#7A7873]">We'll extract totals and items via AI</p>
              </button>
            )}
          </div>

          {/* Vibe */}
          <div>
            <Label>Vibe</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {vibeOptions.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVibe(vibe === v ? '' : v)}
                  className={`occasion-tag transition-colors ${
                    vibe === v
                      ? 'bg-[#C4A35A] text-white'
                      : 'hover:bg-[#E8DCD0]'
                  }`}
                  data-testid={`vibe-${v}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Companions */}
          <div>
            <Label htmlFor="companions">Who were you with? (comma separated)</Label>
            <Input
              id="companions"
              placeholder="e.g., Sarah, James..."
              value={companions}
              onChange={(e) => setCompanions(e.target.value)}
              className="mt-1.5"
              data-testid="companions-input"
            />
          </div>

          {/* Favourite Dishes */}
          <div>
            <Label htmlFor="favouriteDishes">Standout dishes (comma separated)</Label>
            <Input
              id="favouriteDishes"
              placeholder="e.g., Wagyu tartare, Crème brûlée..."
              value={favouriteDishes}
              onChange={(e) => setFavouriteDishes(e.target.value)}
              className="mt-1.5"
              data-testid="favourite-dishes-input"
            />
          </div>

          {/* Journal */}
          <div>
            <Label htmlFor="journal">Journal Entry</Label>
            <Textarea
              id="journal"
              placeholder="Capture the moment — what made this meal memorable?"
              value={journalEntry}
              onChange={(e) => setJournalEntry(e.target.value)}
              className="mt-1.5"
              rows={3}
              data-testid="journal-input"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Quick Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any other notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5"
              rows={2}
              data-testid="notes-input"
            />
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            className="w-full btn-primary"
            disabled={loading || extracting}
            data-testid="submit-visit-btn"
          >
            {loading || extracting ? (
              <>
                <SpinnerGap className="w-5 h-5 animate-spin mr-2" />
                {extracting ? 'Extracting bill...' : 'Saving...'}
              </>
            ) : (
              <>
                <Check weight="bold" className="w-5 h-5 mr-2" />
                {isEditing ? 'Update Visit' : 'Log Visit'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddVisitModal;
